/**
 * App icon and branding utilities for Zapier applications
 * Maps app names to emoji icons and brand colors for visual distinction
 */

export interface AppIconInfo {
  /** Emoji icon representing the app */
  emoji: string;
  /** Brand color (Tailwind class compatible) */
  color: string;
  /** Display name override (if different from key) */
  displayName?: string;
}

/**
 * Comprehensive mapping of popular Zapier apps to emoji icons and colors
 * Emojis provide lightweight, accessible icons without additional assets
 */
export const APP_ICON_MAP: Record<string, AppIconInfo> = {
  // Email & Communication
  'Gmail': { emoji: '📧', color: '#EA4335' },
  'Outlook': { emoji: '📨', color: '#0078D4' },
  'Email': { emoji: '✉️', color: '#6B7280' },
  'SendGrid': { emoji: '📬', color: '#1A82E2' },
  'Mailchimp': { emoji: '🐵', color: '#FFE01B' },

  // CRM & Sales
  'HubSpot': { emoji: '🧲', color: '#FF7A59' },
  'Salesforce': { emoji: '☁️', color: '#00A1E0' },
  'Pipedrive': { emoji: '📊', color: '#1A1A1A' },
  'Zoho CRM': { emoji: '📈', color: '#E42527' },

  // Social Media
  'Facebook Lead Ads': { emoji: '👥', color: '#1877F2' },
  'Facebook': { emoji: '📘', color: '#1877F2' },
  'Twitter': { emoji: '🐦', color: '#1DA1F2' },
  'LinkedIn': { emoji: '💼', color: '#0A66C2' },
  'Instagram': { emoji: '📷', color: '#E4405F' },

  // Messaging
  'Slack': { emoji: '💬', color: '#4A154B' },
  'Discord': { emoji: '🎮', color: '#5865F2' },
  'Telegram': { emoji: '✈️', color: '#26A5E4' },
  'WhatsApp': { emoji: '💚', color: '#25D366' },

  // Spreadsheets & Documents
  'Google Sheets': { emoji: '📊', color: '#34A853' },
  'Airtable': { emoji: '🗂️', color: '#FCB400' },
  'Excel': { emoji: '📗', color: '#217346' },
  'Google Docs': { emoji: '📝', color: '#4285F4' },

  // Forms & Surveys
  'Typeform': { emoji: '📋', color: '#262627' },
  'Google Forms': { emoji: '📃', color: '#673AB7' },
  'JotForm': { emoji: '📄', color: '#FF6100' },
  'SurveyMonkey': { emoji: '🐒', color: '#00BF6F' },

  // E-commerce
  'Shopify': { emoji: '🛍️', color: '#96BF48' },
  'WooCommerce': { emoji: '🛒', color: '#96588A' },
  'Stripe': { emoji: '💳', color: '#635BFF' },
  'PayPal': { emoji: '💰', color: '#00457C' },

  // Project Management
  'Trello': { emoji: '📌', color: '#0079BF' },
  'Asana': { emoji: '✅', color: '#F06A6A' },
  'Monday.com': { emoji: '📅', color: '#FF3D57' },
  'Jira': { emoji: '🎯', color: '#0052CC' },
  'ClickUp': { emoji: '⚡', color: '#7B68EE' },

  // Storage & Files
  'Google Drive': { emoji: '📁', color: '#4285F4' },
  'Dropbox': { emoji: '📦', color: '#0061FF' },
  'OneDrive': { emoji: '☁️', color: '#0078D4' },
  'Box': { emoji: '📤', color: '#0061D5' },

  // Calendar & Scheduling
  'Google Calendar': { emoji: '📆', color: '#4285F4' },
  'Calendly': { emoji: '🗓️', color: '#006BFF' },
  'Outlook Calendar': { emoji: '📅', color: '#0078D4' },

  // Webhooks & Custom
  'Webhooks': { emoji: '🔗', color: '#6B7280' },
  'Webhook': { emoji: '🪝', color: '#6B7280' },
  'Code': { emoji: '💻', color: '#A855F7' },
  'Code by Zapier': { emoji: '⚙️', color: '#FF4A00' },
  'Python': { emoji: '🐍', color: '#3776AB' },
  'JavaScript': { emoji: '🟨', color: '#F7DF1E' },

  // Zapier Internal
  'Filter': { emoji: '🔍', color: '#F59E0B' },
  'Paths': { emoji: '🛤️', color: '#EC4899' },
  'Delay': { emoji: '⏰', color: '#6B7280' },
  'Formatter': { emoji: '🔧', color: '#6B7280' },

  // Other Popular Apps
  'Notion': { emoji: '📓', color: '#000000' },
  'Evernote': { emoji: '🐘', color: '#00A82D' },
  'Twilio': { emoji: '📱', color: '#F22F46' },
  'YouTube': { emoji: '▶️', color: '#FF0000' },
  'Zoom': { emoji: '🎥', color: '#2D8CFF' },
};

/**
 * Normalizes app names for consistent matching
 * Handles case variations and common formatting differences
 */
function normalizeAppName(appName: string): string {
  return appName
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
    // Remove common suffixes
    .replace(/\s*(by zapier|for zapier)\s*$/i, '');
}

/**
 * Gets icon info for an app with fallback to generic icon
 * @param appName - The app name from Zapier data
 * @returns AppIconInfo with emoji and color
 */
export function getAppIcon(appName: string): AppIconInfo {
  // Try exact match first
  if (APP_ICON_MAP[appName]) {
    return APP_ICON_MAP[appName];
  }

  // Try normalized match
  const normalized = normalizeAppName(appName);
  const matchingKey = Object.keys(APP_ICON_MAP).find(
    key => normalizeAppName(key) === normalized
  );

  if (matchingKey) {
    return APP_ICON_MAP[matchingKey];
  }

  // Check for partial matches (e.g., "Gmail for Business" matches "Gmail")
  const partialMatch = Object.keys(APP_ICON_MAP).find(key =>
    normalized.includes(normalizeAppName(key)) ||
    normalizeAppName(key).includes(normalized)
  );

  if (partialMatch) {
    return APP_ICON_MAP[partialMatch];
  }

  // Fallback to generic icon based on app type keywords
  if (normalized.includes('email') || normalized.includes('mail')) {
    return { emoji: '✉️', color: '#6B7280' };
  }
  if (normalized.includes('calendar') || normalized.includes('schedule')) {
    return { emoji: '📅', color: '#6B7280' };
  }
  if (normalized.includes('form')) {
    return { emoji: '📋', color: '#6B7280' };
  }
  if (normalized.includes('sheet') || normalized.includes('spreadsheet')) {
    return { emoji: '📊', color: '#6B7280' };
  }
  if (normalized.includes('crm') || normalized.includes('sales')) {
    return { emoji: '📈', color: '#6B7280' };
  }
  if (normalized.includes('webhook')) {
    return { emoji: '🔗', color: '#6B7280' };
  }

  // Ultimate fallback
  return { emoji: '🔌', color: '#6B7280', displayName: appName };
}

/**
 * Gets a text-based abbreviation for apps without icon support
 * Useful for accessibility labels
 */
export function getAppAbbreviation(appName: string): string {
  const words = appName.split(/\s+/);

  if (words.length === 1) {
    // Single word: take first 2-3 letters
    return appName.substring(0, Math.min(3, appName.length)).toUpperCase();
  }

  // Multiple words: take first letter of each word (up to 3)
  return words
    .slice(0, 3)
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase();
}

/**
 * Checks if an app has a custom icon mapping
 */
export function hasCustomIcon(appName: string): boolean {
  if (APP_ICON_MAP[appName]) return true;

  const normalized = normalizeAppName(appName);
  return Object.keys(APP_ICON_MAP).some(
    key => normalizeAppName(key) === normalized
  );
}
